import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CpmButton from '../../components/design-system/Button';
import CpmCard from '../../components/design-system/Card';
import CpmChip from '../../components/design-system/Chip';
import CpmFilterDropdown from '../../components/design-system/FilterDropdown';
import CpmPageShell from '../../components/design-system/PageShell';
import { listStates } from '../../services/productMasterService';
import { listStarters } from '../../services/starterService';
import type { CpmState } from '../../types/centralProductMaster.types';
import type { StarterSummary } from '../../types/accessManagement.types';
import StateMultiSelect from './components/StateMultiSelect';
import StarterTable from './components/StarterTable';
import { STARTER_COPY } from './starterCopy';

interface StateOption {
    id: string;
    name: string;
    count?: number;
}

function stateLabel(state: CpmState): string {
    return state.name || state.state || state._id;
}

function FunnelIcon({ active }: { active: boolean }) {
    return (
        <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill={active ? 'var(--cpm-brand-purple)' : 'none'}
            stroke={active ? 'var(--cpm-brand-purple)' : 'var(--cpm-text-secondary)'}
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
    );
}

function matchesState(starter: StarterSummary, selected: string[]): boolean {
    if (!selected.length) return true;
    const ids = starter.stateIds || [];
    return ids.some((id) => selected.includes(id));
}

function matchesSign(starter: StarterSummary, selected: string[]): boolean {
    if (!selected.length) return true;
    const dash = selected.includes('dash') && starter.signInDashboard === true;
    const pos = selected.includes('pos') && starter.signInPos === true;
    return dash || pos;
}

function matchesCards(
    starter: StarterSummary,
    every: boolean,
    dash: boolean,
    pos: boolean
): boolean {
    if (every && (starter.stateIds || []).length > 0) return false;
    if (dash && starter.signInDashboard !== true) return false;
    if (pos && starter.signInPos !== true) return false;
    return true;
}

export default function StarterList() {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState<StarterSummary[]>([]);
    const [states, setStates] = useState<CpmState[]>([]);
    const [listState, setListState] = useState<'loading' | 'ready' | 'error'>('loading');
    const [reloadKey, setReloadKey] = useState(0);
    const [query, setQuery] = useState('');
    const [panelOpen, setPanelOpen] = useState(false);
    const [stateOpen, setStateOpen] = useState(false);
    const [signKey, setSignKey] = useState(0);
    const [appliedStates, setAppliedStates] = useState<string[]>([]);
    const [draftStates, setDraftStates] = useState<string[]>([]);
    const [appliedSign, setAppliedSign] = useState<string[]>([]);
    const [draftSign, setDraftSign] = useState<string[]>([]);
    const [cardEvery, setCardEvery] = useState(false);
    const [cardDash, setCardDash] = useState(false);
    const [cardPos, setCardPos] = useState(false);
    const [sortDir, setSortDir] = useState<'asc' | 'desc' | null>(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const [starterResult, stateResult] = await Promise.allSettled([
                    listStarters(),
                    listStates(),
                ]);
                if (cancelled) return;
                if (starterResult.status === 'rejected') {
                    setListState('error');
                    return;
                }
                const rows = starterResult.value?.templates || [];
                setTemplates(
                    rows.map((row) => ({
                        _id: row._id,
                        name: row.name || '',
                        description: row.description || '',
                        stateIds: (row.stateIds || []).map((id) => String(id)),
                        signInDashboard: row.signInDashboard === true,
                        signInPos: row.signInPos === true,
                    }))
                );
                setStates(stateResult.status === 'fulfilled' ? stateResult.value : []);
                setListState('ready');
            } catch {
                if (!cancelled) setListState('error');
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [reloadKey]);

    const stateNames = useMemo(() => {
        const map: Record<string, string> = {};
        states.forEach((state) => {
            map[state._id] = stateLabel(state);
        });
        return map;
    }, [states]);

    const q = query.trim().toLowerCase();
    const base = useMemo(() => {
        return templates.filter((starter) => {
            if (q && !starter.name.toLowerCase().includes(q)) return false;
            if (!matchesState(starter, appliedStates)) return false;
            if (!matchesSign(starter, appliedSign)) return false;
            return true;
        });
    }, [templates, q, appliedStates, appliedSign]);

    const rows = useMemo(() => {
        const filtered = base.filter((starter) => matchesCards(starter, cardEvery, cardDash, cardPos));
        if (!sortDir) return filtered;
        const sign = sortDir === 'asc' ? 1 : -1;
        return filtered
            .slice()
            .sort((left, right) => sign * left.name.localeCompare(right.name));
    }, [base, cardEvery, cardDash, cardPos, sortDir]);

    const counts = {
        published: base.length,
        every: base.filter((starter) => (starter.stateIds || []).length === 0).length,
        dash: base.filter((starter) => starter.signInDashboard === true).length,
        pos: base.filter((starter) => starter.signInPos === true).length,
    };

    const stateOptions: StateOption[] = states.map((state) => ({
        id: state._id,
        name: stateLabel(state),
        count: templates.filter((starter) => (starter.stateIds || []).includes(state._id)).length,
    }));

    const closedLabel = draftStates.length
        ? draftStates.map((id) => stateNames[id] || id).join(', ')
        : STARTER_COPY.ALL_STATES;

    const signLabel = (value: string) =>
        value === 'dash' ? STARTER_COPY.DASHBOARD : STARTER_COPY.POS;

    const onlySearch =
        !!q &&
        appliedStates.length === 0 &&
        appliedSign.length === 0 &&
        !cardEvery &&
        !cardDash &&
        !cardPos;

    const cardsReady = listState !== 'loading' || templates.length > 0;

    const clearAll = () => {
        setQuery('');
        setAppliedStates([]);
        setDraftStates([]);
        setAppliedSign([]);
        setDraftSign([]);
        setCardEvery(false);
        setCardDash(false);
        setCardPos(false);
        setPanelOpen(false);
        setStateOpen(false);
    };

    return (
        <CpmPageShell maxWidth={1440} gap={14} padding="26px 34px 60px">
            <div className="flex items-center justify-between flex-wrap" style={{ gap: 32 }}>
                <h1
                    style={{
                        margin: 0,
                        fontSize: 25,
                        fontWeight: 800,
                        letterSpacing: '-0.022em',
                        lineHeight: 1.1,
                    }}
                >
                    {STARTER_COPY.LIST_TITLE}
                </h1>
                <CpmButton variant="navy" size="md" onClick={() => navigate('/starters/new')}>
                    {STARTER_COPY.CREATE}
                </CpmButton>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
                <HeroCount
                    label={STARTER_COPY.CARD_PUBLISHED}
                    caption={STARTER_COPY.CARD_PUBLISHED_CAPTION}
                    count={counts.published}
                    ready={cardsReady && listState !== 'error'}
                />
                <FilterCount
                    label={STARTER_COPY.CARD_EVERY}
                    caption={STARTER_COPY.CARD_EVERY_CAPTION}
                    count={counts.every}
                    ready={cardsReady && listState !== 'error'}
                    active={cardEvery}
                    onClick={() => setCardEvery((value) => !value)}
                />
                <FilterCount
                    label={STARTER_COPY.CARD_DASH}
                    caption={STARTER_COPY.CARD_DASH_CAPTION}
                    count={counts.dash}
                    ready={cardsReady && listState !== 'error'}
                    active={cardDash}
                    onClick={() => setCardDash((value) => !value)}
                />
                <FilterCount
                    label={STARTER_COPY.CARD_POS}
                    caption={STARTER_COPY.CARD_POS_CAPTION}
                    count={counts.pos}
                    ready={cardsReady && listState !== 'error'}
                    active={cardPos}
                    onClick={() => setCardPos((value) => !value)}
                />
            </div>

            <CpmCard style={{ overflow: 'visible', minHeight: 440 }}>
                <div
                    className="flex flex-col"
                    style={{ gap: 14, padding: '16px 20px', borderBottom: '1px solid var(--cpm-border-default)' }}
                >
                    <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
                        <input
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder={STARTER_COPY.SEARCH_PLACEHOLDER}
                            aria-label={STARTER_COPY.SEARCH_PLACEHOLDER}
                            style={{
                                width: 300,
                                height: 40,
                                boxSizing: 'border-box',
                                padding: '0 12px',
                                borderRadius: 10,
                                border: '1.5px solid var(--cpm-border-strong)',
                                fontSize: 13.5,
                                fontWeight: 700,
                                color: 'var(--cpm-text-primary)',
                                outline: 'none',
                            }}
                        />
                        <CpmButton
                            variant="outline"
                            size="md"
                            onClick={() => {
                                setPanelOpen((open) => {
                                    if (open) {
                                        setStateOpen(false);
                                        return false;
                                    }
                                    setDraftStates(appliedStates);
                                    setDraftSign(appliedSign);
                                    return true;
                                });
                            }}
                        >
                            {STARTER_COPY.FILTER}
                        </CpmButton>
                        {panelOpen ? (
                            <>
                                <CpmButton
                                    variant="navy"
                                    size="md"
                                    onClick={() => {
                                        setAppliedStates(draftStates);
                                        setAppliedSign(draftSign);
                                        setPanelOpen(false);
                                        setStateOpen(false);
                                    }}
                                >
                                    {STARTER_COPY.APPLY}
                                </CpmButton>
                                <CpmButton
                                    variant="outline"
                                    size="md"
                                    onClick={() => {
                                        setDraftStates(appliedStates);
                                        setDraftSign(appliedSign);
                                        setPanelOpen(false);
                                        setStateOpen(false);
                                    }}
                                >
                                    {STARTER_COPY.CANCEL}
                                </CpmButton>
                            </>
                        ) : null}
                        {appliedStates.length ? (
                            <CpmChip
                                variant="token"
                                onRemove={() => {
                                    setAppliedStates([]);
                                    setDraftStates([]);
                                }}
                            >
                                {`${STARTER_COPY.STATE}: ${appliedStates
                                    .map((id) => stateNames[id] || id)
                                    .join(', ')}`}
                            </CpmChip>
                        ) : null}
                        {appliedSign.length ? (
                            <CpmChip
                                variant="token"
                                onRemove={() => {
                                    setAppliedSign([]);
                                    setDraftSign([]);
                                }}
                            >
                                {`${STARTER_COPY.SIGN_IN}: ${appliedSign.map(signLabel).join(', ')}`}
                            </CpmChip>
                        ) : null}
                    </div>
                    {panelOpen ? (
                        <div
                            className="flex flex-wrap"
                            style={{
                                gap: 14,
                                padding: 14,
                                borderRadius: 12,
                                background: 'color-mix(in oklab, #6E64F0 3%, white)',
                                border: '1px solid var(--cpm-border-default)',
                            }}
                        >
                            <div style={{ width: 280, display: 'flex', flexDirection: 'column', gap: 7 }}>
                                <span
                                    style={{
                                        fontSize: 10.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.08em',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {STARTER_COPY.STATE}
                                </span>
                                <StateMultiSelect
                                    mode="filter"
                                    options={stateOptions}
                                    selectedIds={draftStates}
                                    onChange={setDraftStates}
                                    open={stateOpen}
                                    closedLabel={closedLabel}
                                    onOpenChange={(open) => {
                                        setStateOpen(open);
                                        if (open) setSignKey((key) => key + 1);
                                    }}
                                />
                            </div>
                            <div
                                style={{ width: 280 }}
                                onMouseDown={() => setStateOpen(false)}
                            >
                                <CpmFilterDropdown
                                    key={signKey}
                                    label={STARTER_COPY.SIGN_IN}
                                    emptyLabel={STARTER_COPY.ANY_SIGN_IN}
                                    includeEmptyOption={false}
                                    multi
                                    portal
                                    triggerHeight={40}
                                    minWidth={280}
                                    menuMinWidth={280}
                                    options={[
                                        { value: 'dash', label: STARTER_COPY.DASHBOARD },
                                        { value: 'pos', label: STARTER_COPY.POS },
                                    ]}
                                    selected={draftSign}
                                    onChange={setDraftSign}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
                <StarterTable
                    rows={listState === 'ready' ? rows : []}
                    stateNames={stateNames}
                    listState={listState}
                    hasStarters={templates.length > 0}
                    onlySearch={onlySearch}
                    searchLabel={query.trim()}
                    sortDir={sortDir}
                    onSort={() => setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'))}
                    onOpen={(id) => navigate(`/starters/${id}`)}
                    onRetry={() => {
                        setListState('loading');
                        setReloadKey((key) => key + 1);
                    }}
                    onClear={clearAll}
                />
            </CpmCard>
        </CpmPageShell>
    );
}

function HeroCount({
    label,
    caption,
    count,
    ready,
}: {
    label: string;
    caption: string;
    count: number;
    ready: boolean;
}) {
    return (
        <div
            style={{
                padding: '14.5px 16.5px',
                borderRadius: 16,
                border: '1.5px solid transparent',
                background: 'var(--cpm-gradient-hero)',
                boxShadow: '0 1px 2px rgba(14,8,88,0.04), 0 12px 32px -8px rgba(14,8,88,0.28)',
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.72)' }}>
                {label}
            </div>
            <div style={{ height: 38, marginTop: 12, display: 'flex', alignItems: 'center' }}>
                {ready ? (
                    <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: '38px' }}>{count}</div>
                ) : (
                    <div style={{ height: 30, width: 64, borderRadius: 7, background: 'rgba(255,255,255,0.16)' }} />
                )}
            </div>
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.16)', fontSize: 12.5, fontWeight: 600, color: 'rgba(255,255,255,0.78)' }}>
                {caption}
            </div>
        </div>
    );
}

function FilterCount({
    label,
    caption,
    count,
    ready,
    active,
    onClick,
}: {
    label: string;
    caption: string;
    count: number;
    ready: boolean;
    active: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={!ready}
            style={{
                textAlign: 'left',
                padding: '14.5px 16.5px',
                borderRadius: 16,
                border: `1.5px solid ${active ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-default)'}`,
                background: active ? 'color-mix(in oklab, #6E64F0 5%, white)' : '#fff',
                boxShadow: 'var(--cpm-shadow-card)',
                cursor: ready ? 'pointer' : 'default',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div className="flex items-center justify-between" style={{ gap: 8 }}>
                <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {label}
                </span>
                <FunnelIcon active={active} />
            </div>
            <div style={{ height: 38, marginTop: 12, display: 'flex', alignItems: 'center' }}>
                {ready ? (
                    <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: '38px' }}>{count}</div>
                ) : (
                    <div className="cpm-sk" style={{ height: 30, width: 64, borderRadius: 7 }} />
                )}
            </div>
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--cpm-border-default)', fontSize: 12.5, fontWeight: 600, color: 'var(--cpm-text-secondary)' }}>
                {caption}
            </div>
        </button>
    );
}
